/**
 * Missions: staff-planned drone flights (spec §3.5). The planner's `inputs` and `plan` travel as
 * JSON the OpenAPI schema types as unknown; the casts here pin them to the planner's own types.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DroneSpec, MissionInputs, Plan } from '@/planner/types';
import { api, unwrap } from './client';
import type { components } from './schema';

export type MissionRow = components['schemas']['MissionRow'];
type MissionDto = components['schemas']['Mission'];
export interface Mission extends Omit<MissionDto, 'inputs' | 'plan'> {
  inputs: MissionInputs;
  plan: Plan;
}
export interface MissionWrite {
  name: string;
  notes?: string;
  drone: string;
  inputs: MissionInputs;
  plan: Plan;
}
/** An unsaved mission the planner opens prefilled, e.g. a duplicate. */
export type MissionDraft = Pick<MissionWrite, 'name' | 'notes' | 'drone' | 'inputs'>;

const P = (id: string) => ({ params: { path: { public_id: id } } });

export async function listMissionDrones(): Promise<DroneSpec[]> {
  return unwrap(await api.GET('/api/mission-drones')) as DroneSpec[];
}
export async function listMissions(): Promise<MissionRow[]> {
  return unwrap(await api.GET('/api/missions'));
}
export async function getMission(id: string): Promise<Mission> {
  return unwrap(await api.GET('/api/missions/{public_id}', P(id))) as unknown as Mission;
}
export async function createMission(body: MissionWrite): Promise<Mission> {
  return unwrap(await api.POST('/api/missions', { body: body as unknown as components['schemas']['MissionWriteRequest'] })) as unknown as Mission;
}
export async function updateMission(id: string, body: Partial<MissionWrite>): Promise<Mission> {
  return unwrap(
    await api.PATCH('/api/missions/{public_id}', { ...P(id), body: body as unknown as components['schemas']['PatchedMissionWriteRequest'] }),
  ) as unknown as Mission;
}
export async function deleteMission(id: string): Promise<void> {
  unwrap(await api.DELETE('/api/missions/{public_id}', P(id)));
}
/** The KMZ is a plain download; the browser follows the link with its session cookie. */
export function kmzUrl(id: string): string {
  return `/api/missions/${id}/kmz`;
}

export function useMissionDrones() {
  return useQuery({ queryKey: ['mission-drones'], queryFn: listMissionDrones, staleTime: Infinity });
}
export function useMissions() {
  return useQuery({ queryKey: ['missions'], queryFn: listMissions });
}
export function useMission(id: string | undefined) {
  return useQuery({ queryKey: ['missions', id], queryFn: () => getMission(id!), enabled: !!id });
}
export function useCreateMission() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: createMission, onSuccess: () => qc.invalidateQueries({ queryKey: ['missions'] }) });
}
export function useUpdateMission(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<MissionWrite>) => updateMission(id, body),
    onSuccess: (m) => {
      qc.setQueryData(['missions', id], m);
      void qc.invalidateQueries({ queryKey: ['missions'], exact: true });
    },
  });
}
export function useDeleteMission() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: deleteMission, onSuccess: () => qc.invalidateQueries({ queryKey: ['missions'] }) });
}
